var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';
import { useApiInstance } from '@/api/index';
import { Icon } from '@/assets/icons/LibrarySvg';
import CharacterDetailsSidebar from '@/components/Common/CharacterDetailsSidebar';
import CharacterList from '@/components/Common/CharactersList';
import ScreenSaver from '@/components/Common/ScreenSaver';
import DamageHealForm from '@/components/InitiativeTracker/DamageHealForm';
import RoundCounter from '@/components/RoundCounter/RoundCounter';
import Loader from '@/components/UI/Loader';
import SelectCombatMapModal from '@/components/UI/SelectCombatMapModal';
import SwitchToggle from '@/components/UI/SwitchToggle';
import UserSettingsForm from '@/components/UI/UserSettingsForm';
import { useApiContext } from '@/context/ApiContext';
import { useAuth } from '@/context/AuthContext';
import { useCombatLogs } from '@/context/CombatLogsContext';
import { useInitiativeTracker } from '@/context/InitiativeTrackerContext';
import { useCombatMapModal } from '@/hooks/useCombatMapModal';
import { useDamageHealForm } from '@/hooks/useDamageHealForm';
import { CombatLogActionType } from '@/types/combatLogTypes';
import { loadFromLocalStorage, removeFromLocalStorage } from '@/utils/localStorage';
import { isEqualArrays, sortCards } from '@/utils/sort';
const InitiativeTrackerHeader = (props) => {
    var _a, _b;
    const { user, handleNext, toggleAddCardFormVisibility, showAddForm, handleAddCard, handleAddCardToMap, handleUpdateCard, handleChooseCombatMap, pageButtonsContainer, styles, children, } = props;
    const { t } = useTranslation();
    const api = useApiInstance();
    const { userSettings, setUserSettings, fetchUserSettings } = useAuth();
    const { useMockApi } = useApiContext();
    const { cards, setCards, selectedCardsIds, setSelectedCardsIds, userCharacters, setUserCharacters, activeCombatMap, isNextTurnLoading, isOwnerUser, connected, room, } = useInitiativeTracker();
    const { isShowModal: showCombatMapModal, setIsShowModal: setShowCombatMapModal, isLoadingCombatMaps: combatMapsLoading, setLoadingCombatMaps: setCombatMapsLoading, openModal: openCombatMapModal, closeModal: closeCombatMapModal, combatMaps, } = useCombatMapModal();
    const { formState: showDamageHealForm, value: DamageHealValueInput, openForm: openDamageHealForm, closeForm: closeDamageHealForm, increment: incrementValue, decrement: decrementValue, handleChange: handleValueChange, } = useDamageHealForm();
    // Characters
    const [showAddCardPopup, setShowAddCardPopup] = useState(false);
    const [showCharacterList, setShowCharacterList] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [activeCharacter, setActiveCharacter] = useState(null);
    // Screen saver
    const [showScreenSaver, setShowScreenSaver] = useState(false);
    const [screenSaverCharacters, setScreenSaverCharacters] = useState([]);
    const [screenSaverUrl, setScreenSaverUrl] = useState('');
    // Round counters
    const [roundCounters, setRoundCounters] = useState([]);
    const activeCharacterIdx = (_a = sortCards(cards).findIndex((card) => card.id === (activeCharacter === null || activeCharacter === void 0 ? void 0 : activeCharacter.id))) !== null && _a !== void 0 ? _a : -1;
    // CombatLogs
    const { addLog } = useCombatLogs();
    // Загрузка дополнительных счетчиков из localStorage при монтировании
    useEffect(() => {
        const savedCounters = [];
        if (activeCombatMap) {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(`roundCounter-${activeCombatMap.id}`)) {
                    const data = loadFromLocalStorage(key);
                    if (data) {
                        savedCounters.push(data);
                    }
                }
            }
        }
        setRoundCounters(savedCounters);
    }, [activeCombatMap]);
    useEffect(() => {
        setActiveCharacter(cards.find((card) => card.isActive === true) || null);
    }, [cards]);
    // Fetch user settings
    useEffect(() => {
        fetchUserSettings(room === null || room === void 0 ? void 0 : room.code);
    }, [fetchUserSettings, room]);
    const handleNextTurn = () => {
        handleNext();
    };
    // Damage heal
    const applyDamageHealValue = () => __awaiter(void 0, void 0, void 0, function* () {
        if (selectedCardsIds.length === 0)
            return closeDamageHealForm();
        const isDamageType = showDamageHealForm === 'damage';
        const logRecords = [];
        yield Promise.all(selectedCardsIds.map((id) => {
            const card = cards.find((c) => c.id === id);
            if (!card)
                return Promise.resolve();
            const initialValue = Math.abs(DamageHealValueInput);
            let isValueModified = false;
            let value = Math.abs(DamageHealValueInput);
            if (isDamageType) {
                if (card.divideDamage && card.multiplyDamage) {
                    // ignore
                }
                else if (isDamageType && card.divideDamage) {
                    // Спасбросок или сопротивление
                    value = Math.floor(value / 2);
                    isValueModified = true;
                }
                else if (isDamageType && card.multiplyDamage) {
                    // Уязвимость
                    value = Math.floor(value * 2);
                    isValueModified = true;
                }
            }
            let tempHealth = card.tempHealth || 0;
            let health = card.health || 0;
            if (isDamageType) {
                tempHealth = tempHealth - value;
                if (tempHealth < 0) {
                    health = Math.max(0, (card.health || 0) + tempHealth);
                    tempHealth = 0;
                }
            }
            else {
                health = Math.max(0, (card.health || 0) + value);
                if (health === card.health)
                    return Promise.resolve();
                if (card.maxHealth && health > card.maxHealth) {
                    health = card.maxHealth;
                }
            }
            // Собираем логи
            if (activeCharacter && activeCombatMap) {
                const modifiedValueString = isValueModified
                    ? `${card.divideDamage ? ` (${initialValue}/2)` : card.multiplyDamage ? ` (${initialValue}*2)` : ''}`
                    : '';
                logRecords.push({
                    combatMap: activeCombatMap.id,
                    action: isDamageType ? CombatLogActionType.DAMAGE : CombatLogActionType.HEAL,
                    actionCharacter: activeCharacter.name,
                    actionValue: `${value}${modifiedValueString}`,
                    affectedCharacters: card.name,
                    actionResult: `${health}/${card.maxHealth}`,
                });
            }
            const updated = Object.assign(Object.assign({}, card), { health, tempHealth, divideDamage: false, multiplyDamage: false });
            return handleUpdateCard(updated);
        }));
        // Отправка логов
        logRecords.forEach((log) => {
            addLog(log);
        });
        setSelectedCardsIds([]);
        closeDamageHealForm();
    });
    // Обработчик изменения слотов (todo DRY)
    const handleSlotChange = (slotId, action) => __awaiter(void 0, void 0, void 0, function* () {
        if (!slotId)
            return;
        if (selectedCardsIds.length === 0)
            return;
        try {
            const updatedCharacter = yield api.characterApiInstance.updateSpellSlot(activeCharacter.id, slotId, action);
            updatedCharacter.isActive = activeCharacter === null || activeCharacter === void 0 ? void 0 : activeCharacter.isActive;
            updatedCharacter.initiative = (activeCharacter === null || activeCharacter === void 0 ? void 0 : activeCharacter.initiative) || 0;
            if (cards) {
                setCards((prevCards) => prevCards.map((c) => (c.id === updatedCharacter.id ? updatedCharacter : c)));
            }
        }
        catch (e) {
            console.error(e);
        }
    });
    // Fetch screen saver characters & update showScreenSaver state
    useEffect(() => {
        if (!userSettings || !userSettings.screenSaverSettings)
            return;
        const userSettingsScreenSaverCharactersIds = userSettings.screenSaverSettings.charactersIds.sort();
        const screenSaverCharactersIds = screenSaverCharacters.map((char) => char.id).sort();
        if (!isEqualArrays(userSettingsScreenSaverCharactersIds, screenSaverCharactersIds)) {
            if (userSettingsScreenSaverCharactersIds.length === 0) {
                setScreenSaverCharacters([]);
            }
            else {
                api.characterApiInstance
                    .list({ id: userSettings.screenSaverSettings.charactersIds })
                    .then((data) => {
                    setScreenSaverCharacters(sortCards(data));
                });
            }
        }
        setShowScreenSaver(userSettings.screenSaverSettings.enabled);
        if (userSettings.screenSaverSettings.screenSaver !== 'default') {
            setScreenSaverUrl(userSettings.screenSaverSettings.screenSaver);
        }
        else {
            setScreenSaverUrl('');
        }
    }, [api.characterApiInstance, screenSaverCharacters, userSettings]);
    // Fetch user characters
    useEffect(() => {
        if (!user || !isOwnerUser)
            return;
        api.characterApiInstance.list({ created_by_me: true }).then((data) => {
            setUserCharacters(sortCards(data));
        });
        // api.characterGroupsApiInstance.list().then((data) => {
        //   const groups = data.map((group) => ({
        //     ...group,
        //     characters: sortCards(group.characters),
        //   }));
        //   setCharactersGroups(groups);
        // });
    }, [api, isOwnerUser, setUserCharacters, user]);
    // Toggle screen saver
    const handleScreenSaverToggle = (enabled) => __awaiter(void 0, void 0, void 0, function* () {
        const updatedSettings = Object.assign(Object.assign({}, userSettings), { screenSaverSettings: Object.assign(Object.assign({}, userSettings.screenSaverSettings), { enabled: enabled }) });
        try {
            yield api.userApiInstance.updateUserSettings(updatedSettings);
            setUserSettings(updatedSettings);
        }
        catch (e) {
            console.error('Error updating user settings:', e);
        }
    });
    // Fetch combat maps
    const fetchCombatMaps = () => __awaiter(void 0, void 0, void 0, function* () {
        return yield api.combatTrackerApiInstance.combat_map.list({ created_by_id: user === null || user === void 0 ? void 0 : user.id });
    });
    // Handle combat map selection
    const localHandleChooseCombatMap = (map) => __awaiter(void 0, void 0, void 0, function* () {
        setCombatMapsLoading(true);
        handleChooseCombatMap(map);
        setShowCombatMapModal(false);
        setCombatMapsLoading(false);
    });
    // Handle click on "Select Combat Map"
    const handleSelectCombatMap = () => {
        openCombatMapModal(fetchCombatMaps);
    };
    // Close sidebar
    const closeSidebar = () => {
        toggleAddCardFormVisibility();
    };
    const handleAddRoundCounter = (label) => {
        const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const instance = {
            counterIdx: id,
            currentRoundNumber: 1,
            countDownRounds: 0,
            label: label,
        };
        setRoundCounters((prev) => [...prev, instance]);
    };
    const handleAddRoundCountdown = (label, rounds) => {
        const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const instance = {
            counterIdx: id,
            currentRoundNumber: 1,
            countDownRounds: rounds,
            label: label,
        };
        setRoundCounters((prev) => [...prev, instance]);
    };
    const handleRemoveRoundCounter = (id) => {
        setRoundCounters((prev) => prev.filter((c) => c.counterIdx !== id));
        removeFromLocalStorage(`roundCounter-${activeCombatMap.id}-${id}`);
    };
    function handleRoundChange(round) {
        if (activeCombatMap) {
            addLog({
                combatMap: activeCombatMap.id,
                action: CombatLogActionType.NEXT_ROUND,
                actionValue: `${round}`,
                message: `ROUND ${round}`,
            });
        }
    }
    return (_jsxs("div", { className: "initiative-tracker-header", style: styles, children: [_jsx(SelectCombatMapModal, { isOpen: showCombatMapModal, onRequestClose: closeCombatMapModal, combatMapsLoading: combatMapsLoading, combatMaps: combatMaps, handleSelectCombatMap: localHandleChooseCombatMap, contentLabel: t('initiativeTracker.selectCombatMap'), room: room }), _jsxs(ReactModal, { isOpen: showSettings, onRequestClose: () => setShowSettings(false), className: "user-settings-modal", overlayClassName: "user-settings-modal-overlay", shouldCloseOnEsc: true, shouldCloseOnOverlayClick: true, children: [_jsx("button", { className: "close-btn", onClick: () => setShowSettings(false), "aria-label": "close", children: _jsx(Icon, { name: "cross" }) }), _jsx(UserSettingsForm, { onClose: () => setShowSettings(false) })] }), roundCounters &&
                isOwnerUser &&
                createPortal(_jsx("div", { className: "additional-round-counters-container", children: roundCounters.map((rc) => (_jsxs("div", { className: "additional-counter-wrapper", children: [_jsx(RoundCounter, { currentCharacterIdx: activeCharacterIdx, countDownRounds: rc.countDownRounds, isMainCounter: false, label: rc.label, id: rc.counterIdx, onCountDownComplete: () => handleRemoveRoundCounter(rc.counterIdx), combatMapId: activeCombatMap.id }), _jsx("button", { type: "button", className: "remove-counter-btn", onClick: () => {
                                    handleRemoveRoundCounter(rc.counterIdx);
                                }, children: "\u00D7" })] }, rc.counterIdx))) }), document.body), _jsxs("div", { className: "header-container col-3", children: [_jsxs("div", { className: "header-column", children: [_jsx("span", { className: "title", children: t('initiativeTracker.title') }), room && (_jsxs("div", { className: "room-info", children: [_jsxs("span", { className: "room-code", children: [t('rooms.room'), ": ", _jsx("code", { children: room.code })] }), _jsx("span", { className: `connection-status ${connected ? 'online' : 'offline'}`, children: connected ? `${t('rooms.online')}` : `${t('rooms.offline')}` })] }))] }), _jsx("div", { className: "header-column", children: isOwnerUser && (_jsx(RoundCounter, { currentCharacterIdx: activeCharacterIdx, onCreateNewCounter: handleAddRoundCounter, onCreateNewCountdown: handleAddRoundCountdown, isMainCounter: true, pageButtonsContainer: pageButtonsContainer, showToggleButton: true, onRoundChange: handleRoundChange, combatMapId: activeCombatMap.id })) }), user && isOwnerUser && !useMockApi && (_jsxs("div", { className: "header-column", style: { alignItems: 'center', flexDirection: 'row' }, children: [room && (_jsx(SwitchToggle, { checked: showScreenSaver, onChange: handleScreenSaverToggle, labelLeft: t('user.userSettings.screenSaver.title') })), _jsx("button", { className: "settings-btn", onClick: () => setShowSettings(true), children: _jsx(Icon, { name: "settings" }) }), _jsx("div", { className: "select-combat-map-btn", children: _jsx("button", { className: "white-btn", onClick: handleSelectCombatMap, children: t('initiativeTracker.selectCombatMap') }) })] }))] }), (useMockApi || (user && isOwnerUser && !useMockApi)) && (_jsxs("div", { className: "header-buttons col-3", children: [_jsx("div", { className: "header-column", children: _jsx("div", { className: "next-turn-btn", children: _jsx("button", { onClick: () => {
                                    handleNextTurn();
                                }, disabled: cards.length < 2 || isNextTurnLoading, children: isNextTurnLoading ? _jsx(Loader, {}) : t('initiativeTracker.nextTurn') }) }) }), _jsx("div", { className: "header-column", children: _jsx("div", { className: "action-buttons-container", children: showDamageHealForm ? (_jsx(DamageHealForm, { value: DamageHealValueInput, onChange: handleValueChange, onIncrement: incrementValue, onDecrement: decrementValue, onClose: closeDamageHealForm, onApply: applyDamageHealValue, formType: showDamageHealForm, activeCharacter: activeCharacter, onApplySpellSlot: handleSlotChange })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "damage-btn-container", children: _jsx("button", { onClick: () => openDamageHealForm('damage'), children: t('initiativeTracker.damage') }) }), _jsx("div", { className: "heal-btn-container", children: _jsx("button", { onClick: () => openDamageHealForm('heal'), children: t('initiativeTracker.heal') }) })] })) }) }), _jsx("div", { className: "header-column", children: _jsxs("div", { className: "add-card-btn-container", children: [_jsxs("button", { className: "add-card-btn", onClick: () => setShowAddCardPopup(true), children: ["+ ", t('initiativeTracker.addCard')] }), showAddCardPopup && (_jsx("div", { className: "add-card-popup-overlay", onClick: () => {
                                        setShowAddCardPopup(false);
                                        setShowCharacterList(false);
                                    }, children: _jsx("div", { className: "add-card-popup", onClick: (e) => e.stopPropagation(), children: showCharacterList ? (userCharacters.length > 0 ? (_jsx(CharacterList, { characters: userCharacters, onCharacterSelect: handleAddCardToMap, charactersSelected: cards.map((card) => card.id) })) : (t('initiativeTracker.noCharacters'))) : (_jsxs(_Fragment, { children: [_jsx("button", { className: "add-from-characters", onClick: () => setShowCharacterList(true), children: t('initiativeTracker.addFromCharacters') }), _jsx("button", { className: "add-new", onClick: () => {
                                                        setShowAddCardPopup(false);
                                                        toggleAddCardFormVisibility();
                                                    }, children: t('initiativeTracker.addNew') })] })) }) })), createPortal(
                                // Move CharacterDetailsSidebar to the body to avoid overflow issues
                                _jsx(CharacterDetailsSidebar, { isOpen: showAddForm, onClose: closeSidebar, onAdd: handleAddCard, combatMap: activeCombatMap }), document.body)] }) })] })), showScreenSaver &&
                !isOwnerUser &&
                createPortal(_jsx(ScreenSaver, { characters: screenSaverCharacters, imageUrl: screenSaverUrl }), (_b = document.fullscreenElement) !== null && _b !== void 0 ? _b : document.body), children] }));
};
export default InitiativeTrackerHeader;
